/**
 * Маппинг ответа POST /api/dogovor/contract-page → данные для UI.
 * Имена полей совпадают с ключами API (camelCase), без pickFirst.
 * null / undefined / отсутствующие ключи → пустая строка или 0.
 */

const str = (value) => (value == null ? '' : String(value))
const num = (value) => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(+value)) return +value
    return 0
}

const normalizeContract = (data = {}) => ({
    ndog: str(data.ndog),
    datDog: data.datDog ?? null,
    srokDog: str(data.srokDog),
    datNumdog: data.datNumdog ?? null,
    datFin: data.datFin ?? null,
    statusDog: str(data.statusDog)
})

const normalizeBankPayer = (data = {}) => ({
    bplatName: str(data.bplatName),
    bplatRs: str(data.bplatRs),
    bplatBic: str(data.bplatBic),
    bplatKs: str(data.bplatKs)
})

const normalizeBankPayment = (data = {}) => ({
    bpolName: str(data.bpolName),
    bpolRs: str(data.bpolRs),
    bpolBic: str(data.bpolBic),
    bpolKs: str(data.bpolKs)
})

const normalizeCounterparty = (data = {}) => ({
    name: str(data.name),
    inn: str(data.inn),
    kpp: str(data.kpp),
    okved: str(data.okved),
    okpo: str(data.okpo),
    ogrn: str(data.ogrn),
    fioDir: str(data.fioDir),
    tel: str(data.tel),
    telOfficial: str(data.telOfficial),
    emailDog: str(data.emailDog),
    edo: str(data.edo),
    adrUr: str(data.adrUr),
    adrD: str(data.adrD),
    adrDP: str(data.adrDP)
})

const normalizeResponsiblePerson = (data = {}) => ({
    fio: str(data.fio),
    tel: str(data.tel),
    email: str(data.email)
})

const normalizeResponsiblePersons = (data = {}) => ({
    notification: {
        tel: str(data.notification?.tel),
        fax: str(data.notification?.fax),
        email: str(data.notification?.email)
    },
    execution: normalizeResponsiblePerson(data.execution),
    meters: normalizeResponsiblePerson(data.meters),
    payments: normalizeResponsiblePerson(data.payments)
})

const normalizeFinance = (data = {}) => ({
    sumToPay: num(data.sumToPay),
    sumEnergy: num(data.sumEnergy),
    sumPeni: num(data.sumPeni),
    sumImminent: num(data.sumImminent),
    sumTax: num(data.sumTax),
    payDate: data.payDate ?? null
})

export const emptyContractPage = () => ({
    contract: {
        ndog: '',
        datDog: null,
        srokDog: '',
        datNumdog: null,
        datFin: null,
        statusDog: ''
    },
    bankPayer: {
        bplatName: '',
        bplatRs: '',
        bplatBic: '',
        bplatKs: ''
    },
    bankPayment: {
        bpolName: '',
        bpolRs: '',
        bpolBic: '',
        bpolKs: ''
    },
    counterparty: {
        name: '',
        inn: '',
        kpp: '',
        okved: '',
        okpo: '',
        ogrn: '',
        fioDir: '',
        tel: '',
        telOfficial: '',
        emailDog: '',
        edo: '',
        adrUr: '',
        adrD: '',
        adrDP: ''
    },
    filial: {
        filialName: '',
        filialAdr: ''
    },
    manager: {
        fio: '',
        email: '',
        tel: ''
    },
    responsiblePersons: {
        notification: { tel: '', fax: '', email: '' },
        execution: { fio: '', tel: '', email: '' },
        meters: { fio: '', tel: '', email: '' },
        payments: { fio: '', tel: '', email: '' }
    },
    finance: {
        sumToPay: 0,
        sumEnergy: 0,
        sumPeni: 0,
        sumImminent: 0,
        sumTax: 0,
        payDate: null
    }
})

export function mapContractPageResponse(data) {
    if (!data || typeof data !== 'object') return emptyContractPage()

    return {
        contract: normalizeContract(data.contract),
        bankPayer: normalizeBankPayer(data.bankPayer),
        bankPayment: normalizeBankPayment(data.bankPayment),
        counterparty: normalizeCounterparty(data.counterparty),
        filial: {
            filialName: str(data.filial?.filialName),
            filialAdr: str(data.filial?.filialAdr)
        },
        manager: {
            fio: str(data.manager?.fio),
            email: str(data.manager?.email),
            tel: str(data.manager?.tel)
        },
        responsiblePersons: normalizeResponsiblePersons(data.responsiblePersons),
        finance: normalizeFinance(data.finance)
    }
}

/** Слияние contract-page поверх user-dogovors для обратной совместимости */
export function mergeContractPageWithDogovor(contractPage, dogovor) {
    if (!dogovor) return { mergedDogovor: dogovor, page: mapContractPageResponse(contractPage) }
    const page = mapContractPageResponse(contractPage)

    const mergedDogovor = {
        ...dogovor,
        ...page.contract,
        ...page.bankPayer,
        ...page.bankPayment,
        ...page.counterparty,
        ...page.filial
    }

    return { mergedDogovor, page }
}

/** Fallback: собрать contract-page из user-dogovors, пока бэк не отдаёт новый эндпоинт */
export function mapDogovorToContractPage(dogovor) {
    if (!dogovor) return emptyContractPage()

    return mapContractPageResponse({
        contract: {
            ndog: dogovor.ndog,
            datDog: dogovor.datDog,
            srokDog: dogovor.srokDog,
            datNumdog: dogovor.datNumdog,
            datFin: dogovor.datFin,
            statusDog: dogovor.statusDog
        },
        bankPayer: {
            bplatName: dogovor.bplatName,
            bplatRs: dogovor.bplatRs,
            bplatBic: dogovor.bplatBic,
            bplatKs: dogovor.bplatKs
        },
        bankPayment: {
            bpolName: dogovor.bpolName,
            bpolRs: dogovor.bpolRs,
            bpolBic: dogovor.bpolBic,
            bpolKs: dogovor.bpolKs
        },
        counterparty: {
            name: dogovor.name,
            inn: dogovor.inn,
            kpp: dogovor.kpp,
            okved: dogovor.okved,
            okpo: dogovor.okpo,
            ogrn: dogovor.ogrn,
            fioDir: dogovor.fioDir,
            tel: dogovor.tel,
            telOfficial: dogovor.telOfficial,
            emailDog: dogovor.emailDog,
            edo: dogovor.edo,
            adrUr: dogovor.adrUr,
            adrD: dogovor.adrD,
            adrDP: dogovor.adrDP
        },
        filial: {
            filialName: dogovor.filialName,
            filialAdr: dogovor.filialAdr
        }
    })
}
